import { Component, Renderer2, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LanguageService } from '@ibank-globals/language';
import { MetaData } from '@nbg-globals/urlbuilder';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest } from 'rxjs';
import { BaseComponent } from '../../../globals/classes/base.component';
import { AppSettings } from '../../app.settings';
import { Constants } from '../../classes/static/constants';
import { LoginAction, LoginType } from '../../classes/static/enums';
import { Extensions } from '../../classes/static/extensions';
import { LoginService } from '../../services/login.service';
import { ConnectProfileModalComponent } from '../connect-profile-modal/connect-profile-modal.component';
import { IeWarningModalComponent } from '../ie-warning-modal/ie-warning-modal.component';
import { SwitchUsersModalComponent } from '../switch-users-modal/switch-users-modal.component';
import { LockUserWarningModalComponent } from '../lock-user-warning-modal/lock-user-warning-modal.component';

@Component({
	selector: 'new-login',
	template: require('./new-login.component.html'),
	host: { class: 'flex-column min-height-full-vh' },
})
export class NewLoginComponent extends BaseComponent implements OnInit {
	hasImageTitle: boolean = false;
	imageTitleSrc: string = '/Content/icons/logo_iris_white.svg';
	public isFirstTimeLoginPage: boolean = true;
	public action: LoginAction = LoginAction.Login;
	public title: string;
	public loginType: LoginType = null;
	public langId: string;
	public isWatchDemoRetailEnabled: boolean = AppSettings.isWatchDemoRetailEnabled;
	public isWatchDemoCorporateEnabled: boolean = AppSettings.isWatchDemoCorporateEnabled;
	public isGroupUserIdsBigModalEnabled: boolean = AppSettings.isGroupUserIdsBigModalEnabled;
	public demoUrlRetail: string = AppSettings.demoUrlRetail;
	public demoUrlCorporate: string = AppSettings.demoUrlCorporate;
	public isRebrading = AppSettings.isRebrading;
	private _metaReader = new MetaData();
	public warningMessage: string;
	public isWarningMessageEnabled: boolean = false;
	public isBlockUserEnabled: boolean = AppSettings.isBlockUserFeature;

	constructor(
		private _langSvc: LanguageService,
		private renderer: Renderer2,
		private modalService: NgbModal,
		private route: ActivatedRoute
	) {
		super();
	}
	public get displayWatchDemoButton(): boolean {
		if (this.isWatchDemoCorporateEnabled) return this.loginType !== null;
		else if (!this.isWatchDemoCorporateEnabled && !this.isWatchDemoRetailEnabled) return false;
		else {
			return this.loginType == LoginType.Retail;
		}
	}
	public get showLoginTypeSelection(): boolean {
		return this.loginType == null;
	}

	public get showRetailLoginType(): boolean {
		return this.loginType == LoginType.Retail;
	}

	public get showCorpLoginType(): boolean {
		return this.loginType == LoginType.Corporate;
	}

	ngOnInit() {
		this.setupSubscriptions();
		if (this.isFirstTimeLoginPage) {
			this.renderer.addClass(document.body, 'login-select');
		} else {
			if (this.loginType == LoginType.Retail) {
				this.renderer.addClass(document.body, 'login-retail');
			} else if (this.loginType == LoginType.Corporate) {
				this.renderer.addClass(document.body, 'login-business');
			}
		}

		combineLatest(this.route.data, this.route.queryParams).subscribe((result) => {
			const routeData = result[0];
			const routeQueryParams = result[1];

			if (routeData['isImpersonation'] == true) {
				this.action = LoginAction.ImpersonationLogin;
			} else if (routeData['linkNewUser'] === true) {
				this.action = LoginAction.LinkUsers;
			} else if (routeData['switchUser'] === true) {
				this.action = LoginAction.SwitchUser;
			} else if (routeData['renewLink'] === true) {
				this.action = LoginAction.RenewLink;
			}
			LoginService.setAction(this.action);

			let loginType = Extensions.getLoginTypeLowerCase(routeQueryParams);
			if (loginType === 'corporate') {
				LoginService.setType(LoginType.Corporate);
			} else if (loginType === 'retail') {
				LoginService.setType(LoginType.Retail);
			}
			Extensions.setReturnUrl(routeQueryParams);

			const userIdQueryParam = !!routeQueryParams ? routeQueryParams['userId'] : undefined;
			let modalRef: NgbModalRef;

			setTimeout(() => {
				this.setPageTitle();
				if (this.action === LoginAction.SwitchUser && !!userIdQueryParam) {
					modalRef = this.modalService.open(SwitchUsersModalComponent);
				} else if (this.action === LoginAction.RenewLink && !!userIdQueryParam) {
					modalRef = this.modalService.open(ConnectProfileModalComponent, {
						windowClass: !this.isGroupUserIdsBigModalEnabled ? '' : 'modal-full',
					});
					modalRef.componentInstance.isRenew = true;
				}

				if (!!modalRef) {
					modalRef.componentInstance.targetUserId = userIdQueryParam;
				}
			});
		});
		const isIE = /msie\s|trident\//i.test(window.navigator.userAgent);
		if (isIE) {
			this.modalService.open(IeWarningModalComponent);
		}

		this.isWarningMessageEnabled = 'true' === this._metaReader.get('wrnInf').toLowerCase();
		if (this.isWarningMessageEnabled) {
			this.warningMessage = this._metaReader.get('wrnInfMsg');
		}
	}

	private setPageTitle() {
		switch (this.action) {
			case LoginAction.LinkUsers:
				this.title = !this.isRebrading ? 'Σύνδεση προφίλ στο i-bank' : 'Σύνδεση προφίλ';
				break;
			case LoginAction.RenewLink:
				this.title = 'Ανανέωση σύνδεσης προφίλ';
				break;
			case LoginAction.SwitchUser:
				this.title = 'Αλλαγή προφίλ';
				break;
			case LoginAction.ImpersonationLogin:
				this.title = 'Impersonation Login';
				break;
			default:
				//this.title = 'Καλώς ήρθατε στο i-bank';
				this.title = !this.isRebrading ? 'Σύνδεση στο i-bank' : 'Σύνδεση';
				break;
		}
	}

	public watchDemo() {
		if (this.loginType === LoginType.Retail) {
			if (this.langId === 'el') {
				window.open(this.demoUrlRetail + '&lang=el', '_blank');
			} else {
				window.open(this.demoUrlRetail + '&lang=en', '_blank');
			}
		} else {
			if (this.langId === 'el') {
				window.open(this.demoUrlCorporate + '&lang=el', '_blank');
			} else {
				window.open(this.demoUrlCorporate + '&lang=en', '_blank');
			}
		}
	}

	public navigateSecurityGuide(): string {
		if (this.langId === 'el') {
			return 'https://www.nbg.gr/el/footer/ola-osa-xreiazeste-gia-tin-asfaleia-twn-sunallagwn-sas';
		} else
			return 'https://www.nbg.gr/en/footer/everything-you-need-for-the-security-of-your-transactions';
	}

	public openLockUserModal(): void {
		const modalRef = this.modalService.open(LockUserWarningModalComponent, {
			windowClass: 'modal-small',
		});
	}

	private setupSubscriptions() {
		this.mortalize(this._langSvc.activeLanguage$).subscribe((lang) => (this.langId = lang));
		this.mortalize(LoginService.loginType$).subscribe((loginType: LoginType | null) => {
			this.loginType = loginType;
			if (this.loginType == LoginType.Retail) {
				this.renderer.removeClass(document.body, 'login-select');
				this.renderer.removeClass(document.body, 'login-business');
				this.renderer.addClass(document.body, 'login-retail');
			} else if (this.loginType == LoginType.Corporate) {
				this.renderer.removeClass(document.body, 'login-select');
				this.renderer.removeClass(document.body, 'login-retail');
				this.renderer.addClass(document.body, 'login-business');
			} else {
				this.renderer.removeClass(document.body, 'login-business');
				this.renderer.removeClass(document.body, 'login-retail');
				this.renderer.addClass(document.body, 'login-select');
			}
		});
		this.isFirstTimeLoginPage = false;
	}
}
